// The ask-anything bar (AskAnything.dc.html; D74, D47; B181): a search bar across the top of
// triage. Typing and Enter posts to POST /api/ask (the milestone's contract); the answer opens
// as a panel over the dimmed list with "What Cobrain read to answer this" (the sources and
// what was not read) and, when the answer concerns an item, that item's own option set.
// Clearing the bar folds it away. The raw list of everything read is behind the bar's own
// link (U18): "what Cobrain read" opens /read.

import { ActionInput, ActionButton } from './parts/ActionButton.js';
import { useViewing, viewSentence } from './view-as.js';
import { useState, type FormEvent } from 'react';
import { ApiError, Unauthenticated, ask, type AskResult, type Store } from './api.js';
import { dayMonth } from './day.js';
import { kindWord } from './records/shared.js';

export interface AskState { question: string; result: AskResult | null; busy: boolean; error: string | null }

export const askStart: AskState = { question: '', result: null, busy: false, error: null };

/** A phone-width screen, for the shorter placeholder (U31: the words are not cut off by the bar's link). */
const narrow = (): boolean => { try { return window.matchMedia('(max-width: 760px)').matches; } catch { return false; } };

export function AskBar({ store, state, onState, open }: { store: Store; state: AskState; onState: (s: AskState) => void; open: (path: string) => void }) {
  const [text, setText] = useState(state.question);
  // Under the keeper's view the bar is drawn as the person sees it, and asks nothing: no model call is made in their name.
  const view = useViewing();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (view) return;
    const question = text.trim();
    if (!question) { onState(askStart); return; }
    onState({ question, result: null, busy: true, error: null });
    try {
      const result = await ask(store.tenantId, question);
      onState({ question, result, busy: false, error: null });
    } catch (err: unknown) {
      if (err instanceof Unauthenticated) { window.location.href = '/auth/login'; return; }
      onState({ question, result: null, busy: false, error: err instanceof ApiError ? err.message : err instanceof Error ? err.message : String(err) });
    }
  };

  const clear = () => { setText(''); onState(askStart); };

  return (
    <form className={state.result || state.busy ? 'askbar on' : 'askbar'} role="search" onSubmit={(e) => void submit(e)}>
      <svg className="ico" viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>
      <ActionInput aria-label="Ask anything" readOnly={!!view} {...(view ? { title: viewSentence(view) } : {})} placeholder={narrow() ? 'Ask anything' : 'Ask anything about your work, or your life'} value={text}
             onChange={(e) => { setText(e.target.value); if (e.target.value === '' && (state.result || state.error)) onState(askStart); }}
             onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void submit(e); } }} />
      {/* Enter asks; the button is here so the form has a submit control and a screen reader has a name for it. */}
      <ActionButton type="submit" className="link inline ask-go" aria-label="Ask">ask</ActionButton>
      {text || state.result ? <ActionButton reading type="button" className="link inline" onClick={clear}>clear</ActionButton> : null}
      <ActionButton reading type="button" className="link inline" onClick={() => open('/read')}>what Cobrain read</ActionButton>
      {/* U33: Cmd-K reaches the bar from anywhere; the legend says so where there is a keyboard. */}
      <kbd className="ask-kbd" aria-hidden="true">⌘K</kbd>
    </form>
  );
}

/**
 * What is drawn of an answer (U118): its paragraphs, and apart from them the line Cobrain puts under an answer its own check took a
 * sentence from (module 05 R49). The lane sends both as one string, the line last after a blank line, and `leftOut` says the line is
 * there. One `<p>` folded the blank line away, so the line read as the answer's closing sentence, and any answer of two paragraphs (the
 * answer prompt allows two) read as one. An answer of a single paragraph has no such line whatever `leftOut` says: when nothing of an
 * answer was left, the fixed line IS the answer. The words are the lane's, never written here, and they name no subject. The co-pilot
 * panel's typed answers are drawn from this too, so the two read alike.
 */
export function answerParts(result: Pick<AskResult, 'answer' | 'leftOut'>): { paragraphs: string[]; note: string | null } {
  const paragraphs = result.answer.split(/\r?\n(?:[ \t]*\r?\n)+/).map((p) => p.trim()).filter((p) => p.length > 0);
  if (!result.leftOut || paragraphs.length < 2) return { paragraphs, note: null };
  return { paragraphs: paragraphs.slice(0, -1), note: paragraphs[paragraphs.length - 1]! };
}

/** The answer panel over the dimmed list. The item's options are drawn by the caller with the item card. */
export function AskAnswer({ state, children }: { state: AskState; children?: React.ReactNode }) {
  if (!state.busy && !state.result && !state.error) return null;
  const parts = state.result ? answerParts(state.result) : null;
  return (
    <section className="answer" aria-live="polite" data-ask="answer">
      {state.busy ? <p className="note">Reading what Cobrain has on that…</p> : null}
      {state.error ? <p className="plain">Cobrain could not answer that just now. {state.error}</p> : null}
      {state.result ? (
        <>
          {parts!.paragraphs.map((p, i) => <p key={i} className="plain answer-text">{p}</p>)}
          {/* R49, U118: said under the answer, in the voice of a note and never of an error: nothing went wrong, and nothing here can be pressed. */}
          {parts!.note ? <p className="note answer-left-out" data-ask="left-out">{parts!.note}</p> : null}
          {/* The heading is for sources: an answer that rests on nothing has none, and says so in its own words (R43). */}
          {state.result.sources.length > 0 ? <p className="note">What Cobrain read to answer this</p> : null}
          <div className="src">
            {/* The line is the label as the lane draws it ("Email · Will · 4 Sep", "Record · Contract · Kaizen Brazil"): drawn alone. Until 2026-09-20 the
                kind, the person and the date were prefixed to it as well, so every source read twice ("Record · Contract · 31 Dec · Record · Contract · ..."). */}
            {state.result.sources.map((s, i) => <span key={i}>{s.line ? s.line : `${kindWord(s.kind)}${s.who ? ` · ${s.who}` : ''}${s.at ? ` · ${dayMonth(new Date(s.at))}` : ''}`}</span>)}
          </div>
          {/* What was not read stays one press away, never gone (a silence must not be mistaken for an absence), and never six lines under a
              one-line answer: Mike, 2026-09-20, "a whole load of unnecessary crap". The answer itself names the line that matters to it. */}
          {state.result.notRead.length > 0 ? (
            <details className="not-read-fold">
              <summary>What Cobrain did not read ({state.result.notRead.length})</summary>
              <div className="src">{state.result.notRead.map((n, i) => <span key={`n${i}`} className="not-read">{n}</span>)}</div>
            </details>
          ) : null}
          {children}
        </>
      ) : null}
    </section>
  );
}
